/**
 * residency.ts — the residency step's pure half (ADR-124 D1, I3, I9). Common Catalogue
 * copy-fork: `portal-subscriber/src/obw/residency.ts` (canonical) and
 * `portal-staff/src/obw/residency.ts`. No I/O here — the MOUNT loads the served map and supplies
 * it (ADR-107 §2.4: the engine and its steps own no transport).
 *
 * ⭐ EVERY CUSTOMER-FACING STRING BELOW IS ADR-124 I9's, VERBATIM. None is invented here; a
 * change to any of them is a change to the ADR first.
 */

/** The served map, exactly as `GET /portal/zone-map` returns it (ADR-124 I2). */
export interface ZoneMap {
  countries: Record<string, string>;
  zones: Record<string, { label: string }>;
}

/** The outcome of the mount's confirm port (staff mint-in-zone, BOW gate check). */
export type ResidencyConfirm =
  | { ok: true; seed?: Record<string, unknown> }
  | { ok: false; reason: 'gate_closed'; zone: string }
  | { ok: false; reason: 'error' };

/**
 * Everything the residency step needs from its mount. `map`: the served map; `undefined` while it
 * is still loading; `null` when it could not be loaded — the step then says so and cannot
 * continue (fail-closed: a country that cannot be resolved to a zone cannot be minted).
 */
export interface ResidencyContext {
  map: ZoneMap | null | undefined;
  /** A location hint (`cf-ipcountry`) that PRE-FILLS the select. Never auto-confirmed (D1). */
  prefill?: string;
  /**
   * An account-bearing journey: the home already exists and is immutable (D1), so the step is
   * a read-only display of what is stored. `zoneLabel` comes from `/portal/me` (server-side).
   */
  stored?: { jurisdiction?: string; zoneLabel?: string };
  /** Staff only — the console's working zone, for the "created in another zone" notice (D5). */
  workingZone?: { code: string; label: string };
  /** Mount port — runs when the answer is confirmed (staff: mint in the derived zone). */
  confirm?: (jurisdiction: string, zone: string) => Promise<ResidencyConfirm>;
}

// ── ADR-124 I9 — verbatim ────────────────────────────────────────────────────────────────
export const RESIDENCY_LABEL = 'Which country is your business registered in?';
export const RESIDENCY_HELPER = 'This decides where your data is stored and which laws protect it.';
export const sameCountryLine = (zone: string) => `Your data will be stored in: ${zone}`;
export const placedElsewhereLine = (country: string, zone: string) =>
  `Nexanomics stores data for businesses in ${country} in ${zone}.`;
export const closedGateLine = (zone: string) => `Nexanomics is not yet open for sign-ups in ${zone}.`;
export const createdInZoneNotice = (zone: string) => `This account will be created in ${zone}.`;
/**
 * A partner onboarding a participant whose country another zone serves (ADR-124 D3 — the
 * zone-bound commercial policy: a partner serves only the zone it was created in). `{Zone}` is the
 * jurisdiction label, never a region name. Added to I9 on 30 September 2026; it replaced a generic
 * "Could not create the beta account" that did not say why.
 */
export const partnerOtherZoneRefusal = (country: string, zone: string) =>
  `This business is registered in ${country}, which Nexanomics serves from ${zone}. Partners onboard businesses in their own region only.`;

/**
 * A country's English name, from the browser's own ISO 3166 table (`Intl.DisplayNames`) — so no
 * hand-kept list of 249 names can drift from the map. Falls back to the code, never a blank.
 */
export function countryName(code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Every served country as a select option, sorted by name. */
export function countryOptions(map: ZoneMap): { code: string; name: string }[] {
  return Object.keys(map.countries)
    .map((code) => ({ code, name: countryName(code) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The zone that serves `code`, and its jurisdiction label — or null when not placed. */
export function zoneFor(map: ZoneMap, code: string): { zone: string; label: string } | null {
  const zone = map.countries[code];
  if (!zone) return null;
  return { zone, label: map.zones[zone]?.label ?? zone };
}

/**
 * The consequence line (D1): ALWAYS shown, jurisdiction-only, a fact. Same-country when the
 * zone IS that country's own zone — zone codes are country-style by ADR-124 D8 (`au`, `us`), so
 * this is a code comparison, never a string match on a display name.
 */
export function consequenceLine(map: ZoneMap, code: string): string | null {
  const z = zoneFor(map, code);
  if (!z) return null;
  return z.zone === code.toLowerCase() ? sameCountryLine(z.label) : placedElsewhereLine(countryName(code), z.label);
}
